/**
 * 2026-02 — Räume-Tab je Einheit: Grid-Ansicht + Bearbeitungs-Dialog +
 * Fotos-Upload (Emergent Object Storage).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash, PencilSimple, Camera, X, Image as ImageIcon } from "@phosphor-icons/react";

const KIND_LABELS = {
  living: "Wohnzimmer", bedroom: "Schlafzimmer", kitchen: "Küche",
  bathroom: "Bad", toilet: "Gäste-WC", hallway: "Flur", basement: "Keller",
  storage: "Lager / Abstellraum", balcony: "Balkon/Terrasse", garage: "Garage/Stellplatz",
  office: "Büro", meeting: "Besprechungsraum", hall: "Halle", sanitary: "Sanitärraum",
  sales: "Verkaufsraum", social: "Sozialraum", workshop: "Werkstatt", other: "Sonstiges",
};

const emptyRoom = () => ({
  label: "", kind: "other", area_sqm: null, position: 0,
  flooring: "", wall_finish: "", ceiling_finish: "", heating_type: "",
  window_count: null, window_type: "",
  connections: { sockets_count: 0, switches_count: 0, tv: false, phone_dsl: false,
                 ethernet: false, water_cold: false, water_warm: false,
                 washing_machine: false, dishwasher: false },
  sanitary: { bathtub: false, shower: false, wc: false, sink: false,
              bidet: false, handwash_basin: false },
  kitchen: { stove: false, oven: false, hood: false, fridge_niche: false, fitted_kitchen: false },
  security: { smoke_detector: false, lockable_windows: false, roller_shutters: false, balcony_door: false },
  special_features: "", condition_notes: "",
});

export default function UnitRoomsTab({ unitId }) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editRoom, setEditRoom] = useState(null);
  const [presetOpen, setPresetOpen] = useState(false);
  const [presetCount, setPresetCount] = useState(2);
  const [photos, setPhotos] = useState({}); // room_id → [{id, storage_path}]

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/units/${unitId}/rooms`);
      setRooms(data || []);
      // Fotos parallel laden
      const phMap = {};
      await Promise.all((data || []).map(async (r) => {
        try {
          const p = await api.get(`/rooms/${r.id}/photos`);
          phMap[r.id] = p.data || [];
        } catch { phMap[r.id] = []; }
      }));
      setPhotos(phMap);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [unitId]);

  const applyPreset = async () => {
    try {
      await api.post(`/units/${unitId}/rooms/preset`, {
        ...(typeof presetCount === "number" ? { room_count: presetCount } : { preset: presetCount }),
        replace_existing: rooms.length === 0 ? false : true,
      });
      toast.success("Standard-Räume angelegt");
      setPresetOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Preset fehlgeschlagen"); }
  };

  const saveRoom = async () => {
    const body = { ...editRoom };
    delete body.id; delete body.photo_document_ids; delete body.is_deleted;
    delete body.created_at; delete body.updated_at;
    try {
      if (editRoom.id) {
        await api.patch(`/rooms/${editRoom.id}`, body);
        toast.success("Raum aktualisiert");
      } else {
        await api.post(`/units/${unitId}/rooms`, body);
        toast.success("Raum angelegt");
      }
      setEditRoom(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
  };

  const deleteRoom = async (id) => {
    if (!window.confirm("Raum wirklich löschen?")) return;
    try {
      await api.delete(`/rooms/${id}`);
      toast.success("Raum gelöscht");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen"); }
  };

  const uploadPhoto = async (roomId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/rooms/${roomId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Foto hochgeladen");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload fehlgeschlagen"); }
  };

  if (loading) return <div className="p-6 text-sm text-slate-500">Räume werden geladen…</div>;

  return (
    <div className="space-y-3" data-testid="rooms-tab">
      <div className="flex items-center justify-between">
        <div className="text-xs text-slate-500">
          {rooms.length} Raum{rooms.length === 1 ? "" : "e"} · Ausstattungsdetails werden bei Übergabeprotokollen vorgeschlagen.
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setPresetOpen(true)} data-testid="rooms-preset-btn">
            Standard-Räume anlegen
          </Button>
          <Button size="sm" className="gap-1.5 bg-teal-700 hover:bg-teal-800"
                  onClick={() => setEditRoom({ ...emptyRoom(), position: rooms.length })}
                  data-testid="rooms-add-btn">
            <Plus size={12} /> Raum
          </Button>
        </div>
      </div>

      {rooms.length === 0 ? (
        <div className="border border-dashed border-slate-300 rounded-md p-8 text-center text-sm text-slate-500">
          Noch keine Räume erfasst. Nutze „Standard-Räume anlegen" für einen schnellen Einstieg.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {rooms.map((r) => {
            const roomPhotos = photos[r.id] || [];
            return (
              <div key={r.id} className="border border-slate-200 rounded-md bg-white p-3" data-testid={`room-card-${r.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900">{r.label}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">
                      {KIND_LABELS[r.kind] || r.kind}
                      {r.area_sqm ? ` · ${r.area_sqm} m²` : ""}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="h-6 w-6"
                            onClick={() => setEditRoom(r)} data-testid={`room-edit-${r.id}`}>
                      <PencilSimple size={12} />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 hover:text-rose-700"
                            onClick={() => deleteRoom(r.id)} data-testid={`room-delete-${r.id}`}>
                      <Trash size={12} />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 text-[11px] text-slate-600 space-y-0.5">
                  {r.flooring && <div>Boden: {r.flooring}</div>}
                  {r.heating_type && <div>Heizung: {r.heating_type}</div>}
                  {r.window_count > 0 && <div>Fenster: {r.window_count} {r.window_type ? `· ${r.window_type}` : ""}</div>}
                  {r.connections?.sockets_count > 0 && <div>Steckdosen: {r.connections.sockets_count}</div>}
                  {r.sanitary?.bathtub && <span className="mr-1.5 inline-block text-[9px] bg-slate-100 px-1 py-0.5 rounded">Badewanne</span>}
                  {r.sanitary?.shower && <span className="mr-1.5 inline-block text-[9px] bg-slate-100 px-1 py-0.5 rounded">Dusche</span>}
                  {r.kitchen?.fitted_kitchen && <span className="mr-1.5 inline-block text-[9px] bg-slate-100 px-1 py-0.5 rounded">EBK</span>}
                  {r.security?.smoke_detector && <span className="mr-1.5 inline-block text-[9px] bg-slate-100 px-1 py-0.5 rounded">Rauchmelder</span>}
                </div>
                {/* Foto-Grid */}
                <div className="mt-2 flex gap-1.5 flex-wrap items-center">
                  {roomPhotos.slice(0, 5).map((p) => (
                    <RoomPhoto key={p.id} roomId={r.id} photo={p} />
                  ))}
                  <label className="w-10 h-10 rounded border border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:bg-slate-50" title="Foto hochladen">
                    <Camera size={14} className="text-slate-500" />
                    <input type="file" accept="image/*" className="hidden"
                           onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) uploadPhoto(r.id, f); }}
                           data-testid={`room-photo-input-${r.id}`} />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preset-Dialog */}
      <Dialog open={presetOpen} onOpenChange={setPresetOpen}>
        <DialogContent className="max-w-sm" data-testid="preset-dialog">
          <DialogHeader><DialogTitle>Standard-Räume anlegen</DialogTitle></DialogHeader>
          <div className="text-sm space-y-2">
            <Label>Vorlage</Label>
            <Select value={String(presetCount)} onValueChange={(v) => setPresetCount(/^\d+$/.test(v) ? parseInt(v, 10) : v)}>
              <SelectTrigger data-testid="preset-count"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n}-Zimmer-Wohnung</SelectItem>
                ))}
                <SelectItem value="office">Büro</SelectItem>
                <SelectItem value="hall">Halle mit Büro/Lager</SelectItem>
                <SelectItem value="retail">Laden / Verkaufsfläche</SelectItem>
                <SelectItem value="parking">Stellplatz / Garage</SelectItem>
              </SelectContent>
            </Select>
            {rooms.length > 0 && (
              <p className="text-xs text-amber-700 mt-2">
                Achtung: Bestehende Räume werden ersetzt.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPresetOpen(false)}>Abbrechen</Button>
            <Button className="bg-teal-700" onClick={applyPreset} data-testid="preset-apply">Anwenden</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-Dialog */}
      {editRoom && (
        <RoomEditDialog room={editRoom} onChange={setEditRoom} onSave={saveRoom} onClose={() => setEditRoom(null)} />
      )}
    </div>
  );
}


function RoomPhoto({ roomId, photo }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const r = await api.get(`/rooms/${roomId}/photos/${photo.id}/download`, { responseType: "blob" });
        if (!cancel) setUrl(URL.createObjectURL(r.data));
      } catch {}
    })();
    return () => { cancel = true; if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line
  }, [roomId, photo.id]);
  if (!url) return <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center"><ImageIcon size={12} className="text-slate-400" /></div>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="w-10 h-10 rounded overflow-hidden border border-slate-200 hover:opacity-80">
      <img src={url} alt="Raum-Foto" className="w-full h-full object-cover" />
    </a>
  );
}


function RoomEditDialog({ room, onChange, onSave, onClose }) {
  const set = (k, v) => onChange({ ...room, [k]: v });
  const setSub = (grp, k, v) => onChange({ ...room, [grp]: { ...(room[grp] || {}), [k]: v } });
  const Toggle = ({ grp, k, label }) => (
    <label className="flex items-center gap-2 text-xs cursor-pointer">
      <input type="checkbox" className="h-3.5 w-3.5" checked={!!(room[grp] || {})[k]}
             onChange={(e) => setSub(grp, k, e.target.checked)}
             data-testid={`room-${grp}-${k}`} />
      <span>{label}</span>
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="room-edit-dialog">
        <DialogHeader><DialogTitle>{room.id ? "Raum bearbeiten" : "Neuer Raum"}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="md:col-span-2">
              <Label className="text-xs">Bezeichnung *</Label>
              <Input value={room.label} onChange={(e) => set("label", e.target.value)} data-testid="room-label" />
            </div>
            <div>
              <Label className="text-xs">Typ</Label>
              <Select value={room.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger data-testid="room-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(KIND_LABELS).map(([k, l]) => (<SelectItem key={k} value={k}>{l}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Fläche (m²)</Label>
              <Input type="number" step="0.1" value={room.area_sqm || ""}
                     onChange={(e) => set("area_sqm", e.target.value ? parseFloat(e.target.value) : null)}
                     data-testid="room-area" />
            </div>
            <div>
              <Label className="text-xs">Bodenbelag</Label>
              <Input placeholder="Parkett, Laminat, Fliesen…" value={room.flooring || ""}
                     onChange={(e) => set("flooring", e.target.value)} data-testid="room-flooring" />
            </div>
            <div>
              <Label className="text-xs">Heizung</Label>
              <Input placeholder="Zentralheizung, Gastherme…" value={room.heating_type || ""}
                     onChange={(e) => set("heating_type", e.target.value)} data-testid="room-heating" />
            </div>
            <div>
              <Label className="text-xs">Fenster (Anzahl)</Label>
              <Input type="number" value={room.window_count || ""}
                     onChange={(e) => set("window_count", e.target.value ? parseInt(e.target.value, 10) : null)}
                     data-testid="room-window-count" />
            </div>
            <div>
              <Label className="text-xs">Fenster-Typ</Label>
              <Input placeholder="Kunststoff-Isolier, Holz-Doppel…" value={room.window_type || ""}
                     onChange={(e) => set("window_type", e.target.value)} data-testid="room-window-type" />
            </div>
            <div>
              <Label className="text-xs">Wandfarbe/-belag</Label>
              <Input value={room.wall_finish || ""} onChange={(e) => set("wall_finish", e.target.value)} data-testid="room-wall" />
            </div>
          </div>
          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Anschlüsse</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Steckdosen (Anzahl)</Label>
                <Input type="number" value={room.connections?.sockets_count || 0}
                       onChange={(e) => setSub("connections", "sockets_count", parseInt(e.target.value || "0", 10))}
                       data-testid="room-sockets" />
              </div>
              <div>
                <Label className="text-xs">Lichtschalter (Anzahl)</Label>
                <Input type="number" value={room.connections?.switches_count || 0}
                       onChange={(e) => setSub("connections", "switches_count", parseInt(e.target.value || "0", 10))} />
              </div>
              <Toggle grp="connections" k="tv" label="TV-Anschluss" />
              <Toggle grp="connections" k="phone_dsl" label="Telefon/DSL" />
              <Toggle grp="connections" k="ethernet" label="Netzwerk (RJ45)" />
              <Toggle grp="connections" k="water_cold" label="Wasser kalt" />
              <Toggle grp="connections" k="water_warm" label="Wasser warm" />
              <Toggle grp="connections" k="washing_machine" label="Waschmaschine" />
              <Toggle grp="connections" k="dishwasher" label="Spülmaschine" />
            </div>
          </div>
          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Sanitär</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <Toggle grp="sanitary" k="bathtub" label="Badewanne" />
              <Toggle grp="sanitary" k="shower" label="Dusche" />
              <Toggle grp="sanitary" k="wc" label="WC" />
              <Toggle grp="sanitary" k="sink" label="Waschbecken" />
              <Toggle grp="sanitary" k="bidet" label="Bidet" />
              <Toggle grp="sanitary" k="handwash_basin" label="Handwaschbecken" />
            </div>
          </div>
          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Küche</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <Toggle grp="kitchen" k="stove" label="Herd" />
              <Toggle grp="kitchen" k="oven" label="Backofen" />
              <Toggle grp="kitchen" k="hood" label="Dunstabzug" />
              <Toggle grp="kitchen" k="fridge_niche" label="Kühlschrank-Nische" />
              <Toggle grp="kitchen" k="fitted_kitchen" label="Einbauküche" />
            </div>
          </div>
          <div className="border-t pt-2">
            <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1">Sicherheit</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              <Toggle grp="security" k="smoke_detector" label="Rauchmelder" />
              <Toggle grp="security" k="lockable_windows" label="Fenster abschließbar" />
              <Toggle grp="security" k="roller_shutters" label="Rollläden" />
              <Toggle grp="security" k="balcony_door" label="Balkontür" />
            </div>
          </div>
          <div className="border-t pt-2">
            <Label className="text-xs">Besonderheiten</Label>
            <Textarea rows={2} value={room.special_features || ""}
                      onChange={(e) => set("special_features", e.target.value)} data-testid="room-special" />
          </div>
          <div>
            <Label className="text-xs">Zustand / Notizen</Label>
            <Textarea rows={2} value={room.condition_notes || ""}
                      onChange={(e) => set("condition_notes", e.target.value)} data-testid="room-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button className="bg-teal-700 hover:bg-teal-800" onClick={onSave} data-testid="room-save"
                  disabled={!room.label}>
            Speichern
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
